/**
 * This file exposes frequently used pintura methods.
 *
 * These methods are auto-imported in components and pages and can be used
 * directly in templates, <script setup> or anywhere in the Options API without
 * having to manually import them.
 */
import type { Nullable } from '~/types'

/**
 * Decodes width/height via the browser image pipeline (same cache as a
 * follow-up Pintura load for the same URL).
 *
 * @param src The source of the image.
 *
 * @returns Object with the width and height of the image.
 */
async function loadImageNaturalDimensionsFromSrc(
  src: string,
): Promise<{ width: number, height: number } | undefined> {
  const img = new Image()

  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.crossOrigin = 'anonymous'
      img.onerror = () => reject(new Error('image-load'))
      img.src = src
    })

    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      return { width: img.naturalWidth, height: img.naturalHeight }
    }
  }
  catch {
    return undefined
  }

  return undefined
}

/**
 * Reads image dimensions from a file.
 *
 * @param blob The blob of the image file to read.
 *
 * @returns An object with the width & height.
 */
async function readImageDimensionsFromBlob(blob: Blob): Promise<{ width: number, height: number } | undefined> {
  try {
    const bitmap = await createImageBitmap(blob)

    try {
      const { width, height } = bitmap

      if (width > 0 && height > 0) {
        return { width, height }
      }
    }
    finally {
      bitmap.close()
    }
  }
  catch {
    // fall through
  }

  const url = URL.createObjectURL(blob)

  try {
    return await loadImageNaturalDimensionsFromSrc(url)
  }
  finally {
    URL.revokeObjectURL(url)
  }
}

/**
 * Returns an image's dimensions as an object.
 *
 * @param source The source string or blob.
 *
 * @returns The image's width and height.
 */
export async function readImageDimensions(
  source: Nullable<string | Blob | undefined>,
): Promise<{ width: number, height: number } | undefined> {
  if (!source) {
    return undefined
  }

  if (typeof source === 'string') {
    return loadImageNaturalDimensionsFromSrc(source)
  }

  if (source instanceof Blob) {
    return readImageDimensionsFromBlob(source)
  }

  return undefined
}
